import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  FileDown,
  ArrowLeft,
  Loader2
} from 'lucide-react'
import { Button } from '../components/common'
import useSurveyStore from '../stores/surveyStore'
import useCategoryStore from '../stores/categoryStore'
import { generatePDFReport } from '../services/pdfGenerator'
import { generateDocumentReactPdf, triggerDownload } from '../services/reactPdfService'
import { activityDb } from '../db/database'
import { renderAsync } from 'docx-preview'
import PizZip from 'pizzip'
import Docxtemplater from 'docxtemplater'

export default function ReviewSurveyPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [generatingPreview, setGeneratingPreview] = useState(false)
  const [generatingDoc, setGeneratingDoc] = useState(false)
  const [docBlob, setDocBlob] = useState(null)
  const [previewError, setPreviewError] = useState(null)
  const previewContainerRef = useRef(null)

  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const loading = useSurveyStore(state => state.loading)
  const loadSurvey = useSurveyStore(state => state.loadSurvey)
  const clearCurrentSurvey = useSurveyStore(state => state.clearCurrentSurvey)
  const loadCategories = useCategoryStore(state => state.loadCategories)

  // Load survey data and categories
  useEffect(() => {
    if (id) {
      loadSurvey(id)
      loadCategories()
    }
    return () => clearCurrentSurvey()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Generate DOCX preview when survey loads
  useEffect(() => {
    // Only generate when survey is loaded, not already generating, and container exists
    if (currentSurvey && !generatingPreview && !docBlob && previewContainerRef.current) {
      // Add small delay to ensure container is fully mounted
      const timer = setTimeout(() => {
        generatePreview()
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [currentSurvey, generatingPreview, docBlob])

  // Enhance preview by replacing photo placeholders with actual images
  const enhancePreviewWithPhotos = (container, photos) => {
    if (!container || !photos) return

    // Get all text nodes in the container
    const walkTextNodes = (node, callback) => {
      if (node.nodeType === Node.TEXT_NODE) {
        callback(node)
      } else {
        for (let child of node.childNodes) {
          walkTextNodes(child, callback)
        }
      }
    }

    // Track which text nodes to process (avoid modifying while iterating)
    const nodesToProcess = []

    walkTextNodes(container, (textNode) => {
      const text = textNode.textContent
      const photoMatch = text.match(/\[Photo (\d+)\]/)
      if (photoMatch) {
        nodesToProcess.push({ textNode, photoNumber: parseInt(photoMatch[1]) })
      }
    })

    // Process photo placeholders
    nodesToProcess.forEach(({ textNode, photoNumber }) => {
      // Extract category code from surrounding context
      let categoryCode = null
      let currentNode = textNode.parentElement
      while (currentNode && !categoryCode) {
        const contextText = currentNode.textContent
        const categoryMatch = contextText.match(/\b([DEFG]\d)\b/)
        if (categoryMatch) {
          categoryCode = categoryMatch[1]
        }
        currentNode = currentNode.parentElement
      }

      if (categoryCode) {
        const categoryPhotos = photos.filter(p => p.categoryCode === categoryCode)
        const photo = categoryPhotos[photoNumber - 1]

        if (photo) {
          // Create image container - no border, free floating but structured
          const imgWrapper = document.createElement('div')
          imgWrapper.style.cssText = 'margin: 15px 0; width: 100%; max-width: 500px;'

          // Container for consistent sizing
          const imgContainer = document.createElement('div')
          imgContainer.style.cssText = 'width: 100%; height: 250px; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 4px; overflow: hidden;'

          const img = document.createElement('img')
          img.src = photo.thumbnailUrl || photo.imageUrl
          img.alt = `${categoryCode} Photo ${photoNumber}`
          img.style.cssText = 'max-width: 100%; max-height: 100%; object-fit: contain;'

          imgContainer.appendChild(img)
          imgWrapper.appendChild(imgContainer)

          // Add photo notes if available - make highly visible
          if (photo.notes) {
            const notesDiv = document.createElement('div')
            notesDiv.style.cssText = 'margin-top: 10px; padding: 12px 15px; background: #fff8dc; border: 2px solid #ffa500; border-radius: 4px; font-size: 14px; line-height: 1.6; color: #000000; font-weight: 500;'
            notesDiv.innerHTML = `<strong>Photo Note:</strong> ${photo.notes}`
            imgWrapper.appendChild(notesDiv)
          }

          // Replace the text node's parent paragraph with the image
          const parentPara = textNode.parentElement
          if (parentPara) {
            parentPara.parentElement.replaceChild(imgWrapper, parentPara)
          }
        } else {
          // No photo found - remove the placeholder line entirely
          const parentPara = textNode.parentElement
          if (parentPara && parentPara.textContent.trim() === `[Photo ${photoNumber}]`) {
            parentPara.remove()
          }
        }
      }
    })

    // Enhance rating icons with colored circles
    const ratingMap = {
      '①': { bg: '#92D050', text: '1' },
      '②': { bg: '#FFC000', text: '2' },
      '③': { bg: '#FF0000', text: '3' }
    }

    // Find all text nodes with rating icons first
    const ratingNodes = []
    const findRatingNodes = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.match(/[①②③]/)) {
          ratingNodes.push(node)
        }
      } else {
        Array.from(node.childNodes).forEach(child => findRatingNodes(child))
      }
    }
    findRatingNodes(container)

    // Group by category code to only add one badge per category
    const categoryBadges = new Map()

    ratingNodes.forEach((textNode) => {
      const text = textNode.textContent
      const match = text.match(/[①②③]/)

      if (match && textNode.parentElement) {
        const ratingIcon = match[0]
        const rating = ratingMap[ratingIcon]

        // Find category code in surrounding context
        let categoryCode = null
        let searchNode = textNode.parentElement
        let depth = 0
        while (searchNode && !categoryCode && depth < 10) {
          const contextText = searchNode.textContent
          const categoryMatch = contextText.match(/\b([DEFG]\d)\b/)
          if (categoryMatch) {
            categoryCode = categoryMatch[1]
            break
          }
          searchNode = searchNode.parentElement
          depth++
        }

        if (rating) {
          // Remove all rating icons from this text node
          const newText = text.replace(/[①②③]/g, '')
          textNode.textContent = newText

          // Only create badge once per category
          if (categoryCode && !categoryBadges.has(categoryCode)) {
            const badge = document.createElement('span')
            badge.style.cssText = `
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: ${rating.bg};
              color: white;
              font-weight: bold;
              font-size: 24px;
              margin: 0 8px;
              vertical-align: middle;
            `
            badge.textContent = rating.text
            badge.setAttribute('data-rating-badge', categoryCode)

            // Store badge info
            categoryBadges.set(categoryCode, {
              badge,
              insertAfter: textNode.parentElement
            })
          }

          // Remove empty text nodes
          if (newText.trim() === '') {
            textNode.remove()
          }
        }
      }
    })

    // Insert all badges after processing all text nodes
    categoryBadges.forEach(({ badge, insertAfter }) => {
      if (insertAfter && insertAfter.parentElement) {
        insertAfter.parentElement.insertBefore(badge, insertAfter)
      }
    })
  }

  // Generate DOCX preview
  const generatePreview = async () => {
    const container = previewContainerRef.current
    if (!currentSurvey || !container) {
      console.error('Missing survey or container:', { currentSurvey: !!currentSurvey, container: !!container })
      return
    }

    setGeneratingPreview(true)
    setPreviewError(null)

    try {
      // Clear container first
      container.innerHTML = ''

      // Generate DOCX blob (reusing the same function)
      const blob = await generateDOCX(currentSurvey, currentPhotos, currentSurvey.surveyLevel || '3')

      // Render DOCX in the preview container (use original container ref, not current)
      await renderAsync(blob, container, null, {
        className: 'docx-preview',
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        breakPages: true,
        ignoreLastRenderedPageBreak: false,
        experimental: false,
        trimXmlDeclaration: true,
        useBase64URL: false,
        renderChanges: false,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true,
        debug: false
      })

      // Post-process: Replace [Photo X] placeholders with actual images
      enhancePreviewWithPhotos(container, currentPhotos)

      setDocBlob(blob)
    } catch (error) {
      console.error('Failed to generate preview:', error)
      setPreviewError(error.message || 'Failed to generate preview')
    } finally {
      setGeneratingPreview(false)
    }
  }

  // Generate DOCX (without PDF conversion)
  const generateDOCX = async (survey, photos, templateLevel = '3') => {
    const templatePath = `/templates/docx/RICS_LEVEL_${templateLevel}_TEMPLATE.docx?v=11`
    const templateResponse = await fetch(templatePath)

    if (!templateResponse.ok) {
      throw new Error(`Template not found: ${templatePath}`)
    }

    const templateBuffer = await templateResponse.arrayBuffer()

    // Prepare survey data (copy logic from pdfGenerator.js)
    const templateData = await prepareSurveyData(survey, photos)

    // Fill template
    const zip = new PizZip(templateBuffer)
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: '{{', end: '}}' }
    })

    doc.render(templateData)

    const modifiedZip = doc.getZip()
    const docxBlob = modifiedZip.generate({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    })

    return docxBlob
  }

  // Prepare survey data for template (simplified version from pdfGenerator.js)
  const prepareSurveyData = async (survey, photos) => {
    const formatDate = (date) => {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
    }

    const getRatingIconText = (rating) => {
      const icons = { '1': '①', '2': '②', '3': '③', 'NI': 'NI' }
      return icons[rating] || 'NI'
    }

    const getCategoryData = (survey, categoryCode) => {
      const sections = {
        D: survey.sectionDOutside,
        E: survey.sectionEInside,
        F: survey.sectionFServices,
        G: survey.sectionGGrounds
      }
      const sectionKey = categoryCode[0]
      const elementKey = categoryCode.toLowerCase()
      const sectionData = sections[sectionKey]

      if (!sectionData || !sectionData[elementKey]) {
        return { rating: '', comment: '' }
      }

      return {
        rating: sectionData[elementKey].rating || '',
        comment: sectionData[elementKey].comment || ''
      }
    }

    const getAllCategoryCodes = () => {
      return [
        'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8', 'D9',
        'E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7', 'E8', 'E9',
        'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7',
        'G1', 'G2', 'G3'
      ]
    }

    const data = {
      // Basic property information
      PROPERTY_NAME: survey.propertyName || '',
      ADDRESS: survey.address || '',
      POSTCODE: survey.postcode || '',
      PROPERTY_TYPE: survey.propertyType || '',
      CLIENT_NAME: survey.sectionKDeclaration?.clientName || '',
      INSPECTION_DATE: survey.sectionAInspection?.inspectionDate ? formatDate(survey.sectionAInspection.inspectionDate) : '',
      REPORT_REFERENCE: survey.sectionAInspection?.reportReference || '',
      SURVEYOR_RICS_NUMBER: survey.sectionAInspection?.surveyorRicsNumber || '',

      // Section A
      SURVEYOR_NAME: survey.surveyorName || '',
      COMPANY_NAME: survey.sectionAInspection?.companyName || '',
      WEATHER_CONDITIONS: survey.sectionAInspection?.weatherConditions || '',
      PROPERTY_STATUS: survey.sectionAInspection?.propertyStatus || '',
      RELATED_PARTY_DISCLOSURE: survey.sectionAInspection?.relatedPartyDisclosure || '',
      PROPERTY_ADDRESS: `${survey.address || ''}, ${survey.postcode || ''}`,

      // Section B
      overallOpinion: survey.sectionBOpinion || '',
      sectionBDocuments: survey.sectionBDocuments || '',
      sectionBFurtherInvestigations: survey.sectionBFurtherInvestigations || '',

      // Section C
      YEAR_BUILT: survey.sectionCDetails?.yearBuilt || '',
      CONSTRUCTION: survey.sectionCDetails?.construction || '',
      EPC_RATING: survey.sectionCDetails?.energyRating || '',

      // Section H
      H1_REGULATION: survey.sectionHLegal?.h1Regulation || '',
      H2_GUARANTEES: survey.sectionHLegal?.h2Guarantees || '',
      H3_OTHER_MATTERS: survey.sectionHLegal?.h3OtherMatters || '',

      // Section I
      I1_BUILDING_RISKS: survey.sectionIRisks?.i1Building || '',
      I2_GROUNDS_RISKS: survey.sectionIRisks?.i2Grounds || '',
      I3_PEOPLE_RISKS: survey.sectionIRisks?.i3People || '',
      I4_OTHER_RISKS: survey.sectionIRisks?.i4Other || '',

      // Section J
      j1Insulation: survey.sectionJEnergy?.j1Insulation || '',
      j2Heating: survey.sectionJEnergy?.j2Heating || '',
      j3Lighting: survey.sectionJEnergy?.j3Lighting || '',
      j4Ventilation: survey.sectionJEnergy?.j4Ventilation || '',
      j5General: survey.sectionJEnergy?.j5General || '',

      // Section K
      PHONE_NUMBER: survey.sectionKDeclaration?.phone || '',
      EMAIL_ADDRESS: survey.sectionKDeclaration?.email || '',
      SURVEYOR_ADDRESS: survey.sectionKDeclaration?.address || '',
      WEBSITE: survey.sectionKDeclaration?.website || '',
      REPORT_DATE: formatDate(new Date()),

      // Limitations
      D_LIMITATIONS: survey.sectionDOutside?.limitations || '',
      E_LIMITATIONS: survey.sectionEInside?.limitations || '',
      F_LIMITATIONS: survey.sectionFServices?.limitations || '',
      G_LIMITATIONS: survey.sectionGGrounds?.limitations || ''
    }

    // Add category data (D1-G3)
    const categories = getAllCategoryCodes()

    for (const categoryCode of categories) {
      const categoryData = getCategoryData(survey, categoryCode)
      const categoryPhotos = photos.filter(p => p.categoryCode === categoryCode)

      // Only use RATING_ICON for display (circled numbers ①②③)
      // Do NOT set plain RATING to avoid duplicate numbers
      data[`${categoryCode}_RATING`] = getRatingIconText(categoryData.rating || 'NI')
      data[`${categoryCode}_rating`] = getRatingIconText(categoryData.rating || 'NI')
      data[`${categoryCode}_RATING_ICON`] = getRatingIconText(categoryData.rating || 'NI')
      data[`${categoryCode}_COMMENTS`] = categoryData.comment || ''
      data[`${categoryCode}_comment`] = categoryData.comment || ''

      // Rating flags
      data[`${categoryCode}_rating_1`] = categoryData.rating === '1'
      data[`${categoryCode}_rating_2`] = categoryData.rating === '2'
      data[`${categoryCode}_rating_3`] = categoryData.rating === '3'

      // Photo placeholders
      for (let i = 1; i <= 6; i++) {
        const photo = categoryPhotos[i - 1]
        if (photo) {
          data[`${categoryCode}_PHOTO_${i}`] = `[Photo ${i}]`
          data[`${categoryCode}_PHOTO_${i}_NOTE`] = photo.notes || ''
        } else {
          data[`${categoryCode}_PHOTO_${i}`] = ''
          data[`${categoryCode}_PHOTO_${i}_NOTE`] = ''
        }
      }
    }

    return data
  }

  // Generate PDF report - create actual PDF using react-pdf
  const handleGeneratePDFReport = async () => {
    if (!currentSurvey) {
      alert('Please wait for the survey to load before generating PDF')
      return
    }

    setGeneratingDoc(true)
    try {
      // Extract category data from survey sections
      const categoryRatings = {}
      const aiDescriptions = {}
      const limitations = {}

      // Process all categories
      const sections = {
        D: currentSurvey.sectionDOutside,
        E: currentSurvey.sectionEInside,
        F: currentSurvey.sectionFServices,
        G: currentSurvey.sectionGGrounds
      }

      const categoryCodes = [
        'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8', 'D9',
        'E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7', 'E8', 'E9',
        'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7',
        'G1', 'G2', 'G3'
      ]

      for (const code of categoryCodes) {
        const sectionKey = code[0]
        const elementKey = code.toLowerCase()
        const sectionData = sections[sectionKey]

        if (sectionData && sectionData[elementKey]) {
          if (sectionData[elementKey].rating) {
            categoryRatings[code] = sectionData[elementKey].rating
          }
          if (sectionData[elementKey].comment) {
            aiDescriptions[code] = sectionData[elementKey].comment
          }
        }
      }

      // Extract limitations
      if (currentSurvey.sectionDOutside?.limitations) {
        limitations.D_LIMITATIONS = currentSurvey.sectionDOutside.limitations
      }
      if (currentSurvey.sectionEInside?.limitations) {
        limitations.E_LIMITATIONS = currentSurvey.sectionEInside.limitations
      }
      if (currentSurvey.sectionFServices?.limitations) {
        limitations.F_LIMITATIONS = currentSurvey.sectionFServices.limitations
      }
      if (currentSurvey.sectionGGrounds?.limitations) {
        limitations.G_LIMITATIONS = currentSurvey.sectionGGrounds.limitations
      }

      // Generate PDF using react-pdf
      const pdfBlob = await generateDocumentReactPdf(
        currentSurvey,
        currentPhotos,
        aiDescriptions,
        categoryRatings,
        limitations,
        []
      )

      // Download the PDF
      const filename = `${(currentSurvey.propertyName || 'Survey').replace(/[^a-z0-9]/gi, '_')}_Report.pdf`
      triggerDownload(pdfBlob, filename)

      await activityDb.log(currentSurvey.id, 'pdf_report_generated', {
        propertyName: currentSurvey.propertyName,
        surveyLevel: currentSurvey.surveyLevel
      })
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      alert(`Failed to generate PDF: ${error.message}`)
    } finally {
      setGeneratingDoc(false)
    }
  }

  // Download DOCX report
  const handleDownloadDOCX = async () => {
    if (!currentSurvey) return

    setGeneratingDoc(true)
    try {
      // Use the existing docBlob if available, otherwise generate it
      const blob = docBlob || await generateDOCX(currentSurvey, currentPhotos, currentSurvey.surveyLevel || '3')

      const filename = `${(currentSurvey.propertyName || 'Survey').replace(/[^a-z0-9]/gi, '_')}_Report.docx`
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      await activityDb.log(currentSurvey.id, 'docx_downloaded', {
        propertyName: currentSurvey.propertyName,
        surveyLevel: currentSurvey.surveyLevel
      })
    } catch (error) {
      console.error('Failed to download DOCX:', error)
      alert(`Failed to download DOCX: ${error.message}`)
    } finally {
      setGeneratingDoc(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!currentSurvey) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <p className="text-slate-600 mb-4">Survey not found</p>
        <Button onClick={() => navigate('/surveys')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Surveys
        </Button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(`/survey/${id}`)}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Review & Finalize</h1>
                <p className="text-sm text-slate-600">{currentSurvey.propertyName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleGeneratePDFReport}
                disabled={generatingDoc}
                variant="primary"
              >
                {generatingDoc ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <FileDown className="w-4 h-4 mr-2" />
                    Export PDF Report
                  </>
                )}
              </Button>
              <Button
                onClick={handleDownloadDOCX}
                disabled={generatingDoc}
                variant="secondary"
              >
                {generatingDoc ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileDown className="w-4 h-4 mr-2" />
                    Download DOCX
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* DOCX Preview */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {previewError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
            <h3 className="text-red-900 font-semibold mb-2">Preview Error</h3>
            <p className="text-red-700 text-sm whitespace-pre-wrap">{previewError}</p>
            <Button
              onClick={generatePreview}
              variant="outline"
              className="mt-4"
            >
              Try Again
            </Button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-8 relative">
          {generatingPreview && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-90 z-10 rounded-lg">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
              <p className="text-slate-600">Generating document preview...</p>
            </div>
          )}

          <div
            ref={previewContainerRef}
            className="docx-preview-container"
            style={{
              minHeight: '800px'
            }}
          />
        </div>
      </div>
    </div>
  )
}
